/**
 * React Query hooks for import job management
 * Provides polling for import progress and mutation for triggering imports
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { creativesKeys } from '@/lib/queryClient';
import * as metaService from '@/services/meta';
import type { ImportJob, ImportRequest, ImportResponse } from '@/types/meta';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const importJobKeys = {
  all: ['import-jobs'] as const,
  lists: () => [...importJobKeys.all, 'list'] as const,
  list: (projectId: string) => [...importJobKeys.lists(), projectId] as const,
  details: () => [...importJobKeys.all, 'detail'] as const,
  detail: (projectId: string, jobId: string) =>
    [...importJobKeys.details(), projectId, jobId] as const,
};

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch import job status with polling for active jobs
 * Automatically polls every 5 seconds when status is queued or processing
 *
 * Callbacks (onComplete, onPartial, onFailed) are triggered via useEffect
 * to avoid stale closure issues with queryFn.
 */
export function useImportJobStatus(
  projectId?: string,
  jobId?: string,
  options?: {
    enabled?: boolean;
    onComplete?: (job: ImportJob) => void;
    onPartial?: (job: ImportJob) => void;
    onFailed?: (job: ImportJob) => void;
  }
): UseQueryResult<ImportJob, Error> {
  const queryClient = useQueryClient();
  const { enabled = true, onComplete, onPartial, onFailed } = options || {};

  // Track which job IDs we've already fired callbacks for to prevent duplicates
  const handledJobsRef = useRef<Set<string>>(new Set());

  const query = useQuery({
    queryKey: importJobKeys.detail(projectId || '', jobId || ''),
    queryFn: async () => {
      if (!projectId || !jobId) {
        throw new Error('Project ID and Job ID are required');
      }
      return metaService.getImportJob(projectId, jobId);
    },
    enabled: !!projectId && !!jobId && enabled,
    staleTime: 0, // Always refetch when polling
    refetchInterval: (queryState) => {
      const data = queryState.state.data;
      // Poll every 5 seconds if not complete
      if (data?.status === 'queued' || data?.status === 'processing') {
        return 5000;
      }
      return false;
    },
  });

  // Handle status-based callbacks in useEffect to avoid stale closures
  useEffect(() => {
    const job = query.data;
    if (!job || !jobId) return;

    // Check if we've already handled this job's terminal status
    const handledKey = `${jobId}:${job.status}`;
    if (handledJobsRef.current.has(handledKey)) return;

    // Only fire callbacks for terminal statuses
    if (job.status === 'completed') {
      handledJobsRef.current.add(handledKey);
      onComplete?.(job);
      // Invalidate creatives list to show new imports
      if (projectId) {
        queryClient.invalidateQueries({
          queryKey: creativesKeys.list({ projectId }),
        });
      }
    } else if (job.status === 'partial') {
      handledJobsRef.current.add(handledKey);
      onPartial?.(job);
      if (projectId) {
        queryClient.invalidateQueries({
          queryKey: creativesKeys.list({ projectId }),
        });
      }
    } else if (job.status === 'failed') {
      handledJobsRef.current.add(handledKey);
      onFailed?.(job);
    }
  }, [query.data, jobId, projectId, onComplete, onPartial, onFailed, queryClient]);

  // Clear handled jobs when jobId changes (new import started)
  useEffect(() => {
    if (jobId) {
      // Keep only entries for the current jobId
      const newSet = new Set<string>();
      handledJobsRef.current.forEach((key) => {
        if (key.startsWith(`${jobId}:`)) {
          newSet.add(key);
        }
      });
      handledJobsRef.current = newSet;
    }
  }, [jobId]);

  return query;
}

/**
 * List recent import jobs for a project
 * Polls every 5 seconds when any job is active (queued or processing)
 */
export function useImportJobs(
  projectId?: string
): UseQueryResult<ImportJob[], Error> {
  return useQuery({
    queryKey: importJobKeys.list(projectId || ''),
    queryFn: async () => {
      if (!projectId) {
        return [];
      }
      const response = await metaService.listImportJobs(projectId);
      return response.importJobs;
    },
    enabled: !!projectId,
    staleTime: 5 * 1000, // 5 seconds
    refetchInterval: (query) => {
      const data = query.state.data;
      // Poll every 5 seconds if any job is active
      const hasActiveJob = data?.some(
        (job) => job.status === 'queued' || job.status === 'processing'
      );
      return hasActiveJob ? 5000 : false;
    },
  });
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Mutation to trigger import of selected ads
 * Toast notifications are handled in the calling component via onSuccess/onError callbacks
 */
export function useImportMutation(): UseMutationResult<
  ImportResponse,
  Error,
  { projectId: string; adIds: string[]; autoAnalyze?: boolean }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, adIds, autoAnalyze = true }) => {
      const request: ImportRequest = { adIds, autoAnalyze };
      return metaService.importAds(projectId, request);
    },
    onSuccess: (_response, variables) => {
      // Invalidate import jobs list to include new job
      queryClient.invalidateQueries({
        queryKey: importJobKeys.list(variables.projectId),
      });
    },
  });
}
